import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/Button";
import { Card, CardHeader } from "@/components/ui/Card";
import { Dialog } from "@/components/ui/Dialog";
import { Alert, Badge } from "@/components/ui/Feedback";
import { formatDateTime, formatNumber } from "@/lib/format";
import { importsApi, type ImportBatch } from "@/lib/imports-api";

import { importErrorText } from "./errors";

const TONE: Record<ImportBatch["status"], "neutral" | "primary" | "accent" | "danger"> = {
  running: "primary",
  succeeded: "primary",
  failed: "danger",
  rolled_back: "accent",
};

function RollbackDialog({ batch, onClose }: { batch: ImportBatch | null; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const rollback = useMutation({
    mutationFn: importsApi.startRollback,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["imports"] });
      onClose();
    },
  });
  return (
    <Dialog
      open={batch !== null}
      onOpenChange={(open) => !open && onClose()}
      title={t("imports.batches.rollbackTitle", { id: batch?.id })}
      description={t("imports.batches.rollbackBody", {
        new: formatNumber(batch?.counts.tickets_new ?? 0),
        updated: formatNumber(batch?.counts.tickets_updated ?? 0),
      })}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.close")}
          </Button>
          <Button variant="danger" busy={rollback.isPending} onClick={() => rollback.mutate()}>
            {t("imports.batches.rollback")}
          </Button>
        </>
      }
    >
      {rollback.isError ? <Alert>{importErrorText(rollback.error, t)}</Alert> : null}
    </Dialog>
  );
}

export function BatchesCard() {
  const { t } = useTranslation();
  const batches = useQuery({
    queryKey: ["imports", "batches"],
    queryFn: importsApi.batches,
    refetchInterval: 15_000,
  });
  const [undoing, setUndoing] = useState<ImportBatch | null>(null);

  return (
    <Card aria-labelledby="batches-title" className="overflow-hidden">
      <CardHeader title={<span id="batches-title">{t("imports.batches.title")}</span>} />
      <div className="relative overflow-x-auto">
        <table className="stack-table w-full min-w-[44rem] text-left text-sm">
          <thead className="border-b border-line bg-surface-2 text-xs text-ink-muted">
            <tr>
              <th scope="col" className="px-5 py-3 font-medium">
                {t("imports.batches.batch")}
              </th>
              <th scope="col" className="px-5 py-3 font-medium">
                {t("imports.jobs.state")}
              </th>
              <th scope="col" className="px-5 py-3 font-medium">
                {t("imports.batches.tickets")}
              </th>
              <th scope="col" className="px-5 py-3 font-medium">
                {t("imports.jobs.when")}
              </th>
              <th scope="col" className="px-5 py-3 font-medium">
                <span className="sr-only">{t("imports.files.actions")}</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {batches.data?.map((batch) => (
              <tr key={batch.id} className="align-top">
                <td className="px-5 py-3">
                  <div className="font-medium">#{batch.id}</div>
                  <div className="text-xs break-all text-ink-subtle">{batch.source_file}</div>
                </td>
                <td className="px-5 py-3" data-label={t("imports.jobs.state")}>
                  <div className="flex flex-wrap gap-1.5">
                    <Badge tone={TONE[batch.status]}>{t(`imports.batches.status.${batch.status}`)}</Badge>
                    {batch.dry_run && <Badge tone="accent">{t("imports.jobs.dryRun")}</Badge>}
                  </div>
                </td>
                <td className="px-5 py-3 text-ink-muted" data-label={t("imports.batches.tickets")}>
                  {t("imports.jobs.counts", {
                    new: formatNumber(batch.counts.tickets_new ?? 0),
                    updated: formatNumber(batch.counts.tickets_updated ?? 0),
                    unchanged: formatNumber(batch.counts.tickets_unchanged ?? 0),
                  })}
                  {batch.error && <div className="text-xs text-danger">{batch.error}</div>}
                </td>
                <td className="px-5 py-3 text-ink-muted tabular-nums" data-label={t("imports.jobs.when")}>
                  {formatDateTime(batch.started_at)}
                </td>
                <td className="px-5 py-3 text-right whitespace-nowrap max-sm:pt-2">
                  {batch.can_rollback && (
                    <Button variant="danger" onClick={() => setUndoing(batch)}>
                      {t("imports.batches.rollback")}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {batches.data?.length === 0 && (
          <p className="px-5 py-6 text-sm text-ink-muted">{t("imports.batches.empty")}</p>
        )}
      </div>
      <RollbackDialog batch={undoing} onClose={() => setUndoing(null)} />
    </Card>
  );
}
